// ============================================================
// TPO-Engine (clientseitig) — baut aus Klines ein Tages-TPO-Profil.
//
//   • Brackets je `bracketMin` (Default 30) → Buchstabe A,B,C…
//   • Preis in Buckets der Größe `rowSize`
//   • POC / VAH / VAL (Value-Area %), Initial Balance (erste 2 Brackets),
//     Single Prints, ST/BT-Excess-Tails an den Extremen
//   • Tagesgrenze fix 00:00 UTC (= 02:00 UTC+2)
// ============================================================
import { Bar } from './types';

const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
export function letterFor(b: number): string { return LETTERS[b] ?? '·'; }

export interface TpoSettings {
  bracketMin: number;    // Minuten je Bracket (Default 30)
  valueAreaPct: number;  // Value-Area-Anteil (Default 70)
  rowSize: number;       // Preis-Bucket-Größe
}

export interface TpoRow {
  bucket: number;        // ganzzahliger Bucket-Index
  price: number;         // untere Kante des Buckets
  brackets: number[];    // enthaltene Bracket-Indizes (sortiert)
  count: number;
  isPoc: boolean;
  inVa: boolean;
  singlePrint: boolean;
  excess: boolean;       // Teil eines ST/BT-Single-Print-Tails
}

export interface TpoProfile {
  dayStartSec: number;
  rowSize: number;
  bracketCount: number;
  rows: TpoRow[];        // aufsteigend nach Preis
  poc: number;
  vah: number;
  val: number;
  ibHigh: number;
  ibLow: number;
  hi: number;            // Tageshoch
  lo: number;            // Tagestief
}

// 00:00 UTC des Tages, in dem `sec` liegt.
export function dayStartUTC(sec: number): number {
  return Math.floor(sec / 86400) * 86400;
}

// Baut ein TPO-Profil aus Bars EINES Tages (dayStartSec = 00:00 UTC dieses Tages).
export function buildProfile(bars: Bar[], s: TpoSettings, dayStartSec: number): TpoProfile | null {
  if (!bars.length || s.rowSize <= 0) return null;
  const bracketSec = s.bracketMin * 60;

  // Bucket-Index -> Set der Bracket-Indizes (ein Buchstabe je Bracket je Bucket).
  const map = new Map<number, Set<number>>();
  let bracketCount = 0;
  let hi = -Infinity, lo = Infinity;
  let ibHigh = -Infinity, ibLow = Infinity;

  for (const b of bars) {
    const sec = Math.floor(b.openTime / 1000);
    const bracket = Math.floor((sec - dayStartSec) / bracketSec);
    if (bracket < 0) continue;
    if (bracket + 1 > bracketCount) bracketCount = bracket + 1;

    hi = Math.max(hi, b.h); lo = Math.min(lo, b.l);
    if (bracket < 2) { ibHigh = Math.max(ibHigh, b.h); ibLow = Math.min(ibLow, b.l); }

    const loBucket = Math.floor(b.l / s.rowSize);
    const hiBucket = Math.floor(b.h / s.rowSize);
    for (let bk = loBucket; bk <= hiBucket; bk++) {
      let set = map.get(bk);
      if (!set) { set = new Set(); map.set(bk, set); }
      set.add(bracket);
    }
  }
  if (!map.size) return null;

  const rows: TpoRow[] = Array.from(map.entries())
    .map(([bucket, set]) => ({
      bucket,
      price: bucket * s.rowSize,
      brackets: Array.from(set).sort((a, b) => a - b),
      count: set.size,
      isPoc: false, inVa: false, singlePrint: set.size === 1, excess: false,
    }))
    .sort((a, b) => a.bucket - b.bucket);

  const { poc, vah, val } = computeLevels(rows, s.valueAreaPct, s.rowSize);

  return {
    dayStartSec, rowSize: s.rowSize, bracketCount, rows,
    poc, vah, val,
    ibHigh: isFinite(ibHigh) ? ibHigh : 0,
    ibLow: isFinite(ibLow) ? ibLow : 0,
    hi: isFinite(hi) ? hi : 0,
    lo: isFinite(lo) ? lo : 0,
  };
}

// Setzt POC/VA/Excess-Flags auf den (preisaufsteigend sortierten) Reihen und gibt
// POC/VAH/VAL zurück. Geteilt von buildProfile und finalizeProfile, damit beide
// Pfade identisch rechnen.
function computeLevels(rows: TpoRow[], valueAreaPct: number, rowSize: number): { poc: number; vah: number; val: number } {
  if (!rows.length) return { poc: 0, vah: 0, val: 0 };

  // POC = höchste Count-Reihe.
  let pocIdx = 0;
  for (let i = 1; i < rows.length; i++) if (rows[i].count > rows[pocIdx].count) pocIdx = i;
  rows[pocIdx].isPoc = true;

  // Value Area: von POC aus expandieren bis valueAreaPct erreicht.
  const total = rows.reduce((sum, r) => sum + r.count, 0);
  const target = Math.ceil((total * valueAreaPct) / 100);
  let vaCount = rows[pocIdx].count;
  let hiI = pocIdx, loI = pocIdx;
  while (vaCount < target) {
    const above = hiI + 1 < rows.length ? rows[hiI + 1].count : 0;
    const below = loI - 1 >= 0 ? rows[loI - 1].count : 0;
    if (above === 0 && below === 0) break;
    if (above >= below) { hiI++; vaCount += above; }
    else { loI--; vaCount += below; }
  }
  for (let i = loI; i <= hiI; i++) rows[i].inVa = true;

  // ST/BT-Excess: zusammenhängende Single-Print-Tails an den Extremen (≥2).
  let topRun = 0;
  for (let i = rows.length - 1; i >= 0 && rows[i].count === 1; i--) topRun++;
  if (topRun >= 2) for (let i = rows.length - topRun; i < rows.length; i++) rows[i].excess = true;
  let botRun = 0;
  for (let i = 0; i < rows.length && rows[i].count === 1; i++) botRun++;
  if (botRun >= 2) for (let i = 0; i < botRun; i++) rows[i].excess = true;

  return {
    poc: rows[pocIdx].price + rowSize / 2,
    vah: rows[hiI].price + rowSize, // obere Kante
    val: rows[loI].price,           // untere Kante
  };
}

// ============================================================
// Inkrementelles TPO (Block C)
// Abgeschlossene Brackets werden EINMAL zu einer Verteilung aggregiert und
// gecacht (TpoAccumulator). Pro 1-s-Tick wird über finalizeProfile NUR das
// laufende Bracket eingefaltet — die abgeschlossenen Sets bleiben unangetastet.
// ============================================================

export interface TpoAccumulator {
  dayStartSec: number;
  rowSize: number;
  bracketMin: number;
  valueAreaPct: number;
  buckets: Map<number, Set<number>>; // bucket -> Set abgeschlossener Bracket-Indizes
  bracketCount: number;              // höchster abgeschlossener Bracket + 1
  hi: number; lo: number;
  ibHigh: number; ibLow: number;
}

// Das laufende (noch offene) 30-min-Bracket aus Live-Ticks.
export interface RunningBracket {
  bracketIdx: number;
  hi: number;
  lo: number;
}

// Aggregiert NUR abgeschlossene Bars zu einer gecachten Verteilung. Wird selten
// aufgerufen (Bracket-Schluss / periodischer Resync), nicht pro Sekunde.
export function buildAccumulator(bars: Bar[], s: TpoSettings, dayStartSec: number): TpoAccumulator {
  const bracketSec = s.bracketMin * 60;
  const buckets = new Map<number, Set<number>>();
  let bracketCount = 0;
  let hi = -Infinity, lo = Infinity, ibHigh = -Infinity, ibLow = Infinity;

  for (const b of bars) {
    const sec = Math.floor(b.openTime / 1000);
    const bracket = Math.floor((sec - dayStartSec) / bracketSec);
    if (bracket < 0) continue;
    if (bracket + 1 > bracketCount) bracketCount = bracket + 1;
    hi = Math.max(hi, b.h); lo = Math.min(lo, b.l);
    if (bracket < 2) { ibHigh = Math.max(ibHigh, b.h); ibLow = Math.min(ibLow, b.l); }

    const loBucket = Math.floor(b.l / s.rowSize);
    const hiBucket = Math.floor(b.h / s.rowSize);
    for (let bk = loBucket; bk <= hiBucket; bk++) {
      let set = buckets.get(bk);
      if (!set) { set = new Set(); buckets.set(bk, set); }
      set.add(bracket);
    }
  }

  return {
    dayStartSec, rowSize: s.rowSize, bracketMin: s.bracketMin, valueAreaPct: s.valueAreaPct,
    buckets, bracketCount,
    hi: isFinite(hi) ? hi : 0, lo: isFinite(lo) ? lo : 0,
    ibHigh: isFinite(ibHigh) ? ibHigh : 0, ibLow: isFinite(ibLow) ? ibLow : 0,
  };
}

// Erzeugt das vollständige Profil aus gecachten Brackets + (optional) dem laufenden
// Bracket. Nur das laufende Bracket wird hier rasterisiert.
export function finalizeProfile(acc: TpoAccumulator, run: RunningBracket | null): TpoProfile | null {
  const rowSize = acc.rowSize;

  // Buckets, die das laufende Bracket berührt — der EINZIGE Neu-Rechenanteil pro Tick.
  const runBuckets = new Set<number>();
  if (run && isFinite(run.hi) && isFinite(run.lo)) {
    const loBucket = Math.floor(run.lo / rowSize);
    const hiBucket = Math.floor(run.hi / rowSize);
    for (let bk = loBucket; bk <= hiBucket; bk++) runBuckets.add(bk);
  }
  if (!acc.buckets.size && !runBuckets.size) return null;

  const allBuckets = new Set<number>(acc.buckets.keys());
  runBuckets.forEach(bk => allBuckets.add(bk));

  const rows: TpoRow[] = Array.from(allBuckets).sort((a, b) => a - b).map(bucket => {
    const set = acc.buckets.get(bucket);
    const brackets = set ? Array.from(set) : [];
    if (run && runBuckets.has(bucket)) brackets.push(run.bracketIdx);
    brackets.sort((a, b) => a - b);
    return {
      bucket, price: bucket * rowSize, brackets, count: brackets.length,
      isPoc: false, inVa: false, singlePrint: brackets.length === 1, excess: false,
    };
  });

  const { poc, vah, val } = computeLevels(rows, acc.valueAreaPct, rowSize);

  let ibHigh = acc.ibHigh, ibLow = acc.ibLow;
  if (run && run.bracketIdx < 2) {
    ibHigh = ibHigh > 0 ? Math.max(ibHigh, run.hi) : run.hi;
    ibLow = ibLow > 0 ? Math.min(ibLow, run.lo) : run.lo;
  }
  const hi = run ? Math.max(acc.hi, run.hi) : acc.hi;
  const lo = run ? (acc.lo > 0 ? Math.min(acc.lo, run.lo) : run.lo) : acc.lo;
  const bracketCount = run ? Math.max(acc.bracketCount, run.bracketIdx + 1) : acc.bracketCount;

  return { dayStartSec: acc.dayStartSec, rowSize, bracketCount, rows, poc, vah, val, ibHigh, ibLow, hi, lo };
}

// Auto-Row-Size aus Tick-Size: feiner Tick → größerer Bucket, ~> sinnvolle Reihenzahl.
export function autoRowSize(tickSize: number, refPrice: number): number {
  if (tickSize > 0 && refPrice > 0) {
    // Ziel: ~0.05% des Preises je Reihe, auf ein Vielfaches der Tick-Size gerundet.
    const target = refPrice * 0.0005;
    const mult = Math.max(1, Math.round(target / tickSize));
    return mult * tickSize;
  }
  return tickSize || 0.01;
}
